/* eslint-disable no-mixed-spaces-and-tabs */
import { useCallback, useContext, useEffect, useMemo, useReducer } from "react";
import { useDispatch } from "react-redux";

import {
  FILTER_MANAGER_KEY,
  FILTER_WORK_CENTERS_KEY,
} from "../../../assets/constants";
import { FILTER_WORKER_TYPE_KEY } from "../../../assets/constants";
import { WORKER_TYPES } from "../../filters/constants";
import { Filter } from "../../filters/filter-accordion/types";
import { FilterContext } from "../../filters/filter-context";
import {
  fetchFTWorkerFilters,
  fetchFTWorkerShifts,
  setSearchTerm,
} from "../store/worker-scheduler-slice";
import { getFilteredWorkers } from "../utils";
import useWorkerSchedulerData from "./use-worker-scheduler-data";

const OPEN_WORKCENTER = "OPEN_WORKCENTER";
const CLOSE_WORKCENTER = "CLOSE_WORKCENTER";
const OPEN_SHIFT_PATTERN = "OPEN_SHIFT_PATTERN";
const CLOSE_SHIFT_PATTERN = "CLOSE_SHIFT_PATTERN";
const OPEN_PRIMARY_JOB = "OPEN_PRIMARY_JOB";
const CLOSE_PRIMARY_JOB = "CLOSE_PRIMARY_JOB";
const OPEN_WORKER_TYPE = "OPEN_WORKER_TYPE";
const CLOSE_WORKER_TYPE = "CLOSE_WORKER_TYPE";

const WORKCENTER = "WORKCENTER";
const SHIFT_PATTERN = "SHIFT_PATTERN";

interface State {
  openWorkcenters: number[];
  openShiftPatterns: string[];
  openPrimaryJobs: string[];
  openWorkerTypes: number[];
}

type Action =
  | { type: typeof OPEN_SHIFT_PATTERN; payload: string }
  | { type: typeof CLOSE_SHIFT_PATTERN; payload: string }
  | { type: typeof OPEN_WORKCENTER; payload: number }
  | { type: typeof CLOSE_WORKCENTER; payload: number }
  | { type: typeof OPEN_PRIMARY_JOB; payload: string }
  | { type: typeof CLOSE_PRIMARY_JOB; payload: string }
  | { type: typeof OPEN_WORKER_TYPE; payload: number }
  | { type: typeof CLOSE_WORKER_TYPE; payload: number };

const initialState: State = {
  openShiftPatterns: [],
  openWorkcenters: [],
  openPrimaryJobs: [],
  openWorkerTypes: [],
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case OPEN_SHIFT_PATTERN:
    case OPEN_WORKCENTER:
    case OPEN_PRIMARY_JOB:
    case OPEN_WORKER_TYPE:
      if (!state[getStateKey(action.type)].includes(action.payload as never)) {
        const key = getStateKey(action.type);
        return {
          ...state,
          [key]: [
            ...state[key],
            action.payload,
          ],
        };
      }
      return state;
    case CLOSE_SHIFT_PATTERN:
    case CLOSE_WORKCENTER:
    case CLOSE_WORKER_TYPE:
    case CLOSE_PRIMARY_JOB: {
      const closeKey = getStateKey(action.type);
      return {
        ...state,
        [closeKey]: (state[closeKey] as any[]).filter(
          (id: string | number) => id !== action.payload
        ),
      };
    }
    default:
      return state;
  }
}

function getStateKey(actionType: string): keyof State {
  switch (actionType) {
    case OPEN_SHIFT_PATTERN:
    case CLOSE_SHIFT_PATTERN:
      return "openShiftPatterns";
    case OPEN_WORKCENTER:
    case CLOSE_WORKCENTER:
      return "openWorkcenters";
    case OPEN_PRIMARY_JOB:
    case CLOSE_PRIMARY_JOB:
      return "openPrimaryJobs";
    case OPEN_WORKER_TYPE:
    case CLOSE_WORKER_TYPE:
      return "openWorkerTypes";
    default:
      throw new Error(`Unknown action type: ${actionType}`);
  }
}

const useWorkerSchedulerController = ({
                                        isPrinting = false,
                                      }: { isPrinting?: boolean } = {}) => {
  const dispatch = useDispatch();
  const [state, localDispatch] = useReducer(reducer, initialState);
  const viewMode: [string, string] = [WORKCENTER, SHIFT_PATTERN];

  const filterContext = useContext(FilterContext);
  if (!filterContext) {
    throw new Error("FilterContext must be used within a FilterProvider");
  }
  const {
    appliedFilters,
    open,
    filters,
    setFilterOpen,
    setAppliedFilters,
    setFilters,
    setDataRelations,
  } = filterContext;

  const {
    filterData,
    fetchFTWorkerFiltersStatus,
    fetchFTWorkerShiftsStatus,
    startingDay,
    ftWorkerShifts,
    ftWorkerFlexShifts,
    selectedLocation,
    allWorkers,
    currentUserId,
  } = useWorkerSchedulerData();

  const {
    allWorkcenters,
    shiftPatternsWithDates,
    workerTypes,
    shiftPatternsObj,
    schedulingStatusesObj,
  } = filterData;

  const filteredWorkers = useMemo(
    () => getFilteredWorkers(allWorkers, appliedFilters),
    [allWorkers, appliedFilters]
  );

  const allShifts = useMemo(() => {
    return [
      ...(ftWorkerShifts ?? []).map((s) => ({ ...s, isFlexShift: false })),
      ...(ftWorkerFlexShifts ?? []).map((s) => ({ ...s, isFlexShift: true })),
    ];
  }, [ftWorkerShifts, ftWorkerFlexShifts]);

  // Main logic for building the map
  const workerWorkcenterShiftPatternsMap = useMemo(() => {
    // --- Setup ---
    const flexTypeId = workerTypes.find(
      (type) => type.type === "FlexWorker"
    )?.id;

    const workcenterDataMap = new Map();

    const locationWorkcenters = allWorkcenters.filter(
      (wc) => wc.locationId === selectedLocation && wc.id
    );
    locationWorkcenters.forEach((wc) => {
      workcenterDataMap.set(wc.id, {
        ...wc,
        shiftPatternIds: new Set(wc.shiftPatterns || []),
        workersByPatternJob: new Map(),
        totalWorkersCount: new Set(),
      });
    });

    // --- Helper function to place a worker in the map ---
    const placeWorker = (
      worker: any,
      shifts: any[],
      wcId: number,
      patternId: number | null,
      jobId: number | null,
      isClone: boolean, // 'isClone' also means 'isMoved' in this context
      cloneKeySuffix = ""
    ) => {
      const wcData = workcenterDataMap.get(wcId);
      if (!wcData) return;

      wcData.totalWorkersCount.add(worker.workerId);

      const workerData = {
        ...worker,
        shifts: shifts,
        // These flags are critical for the print/export view to show (WC), (J), (SP) tags
        isWorkcenterChanged: isClone
          ? wcId !== (worker.workCenterId || null)
          : false,
        isJobChanged: isClone
          ? jobId !== (worker.primaryJobId || null)
          : false,
        isPatternChanged: isClone
          ? patternId !== (worker.patternId || null)
          : false,
      };

      const targetSpId: number | null =
        patternId && wcData.shiftPatternIds.has(patternId)
          ? patternId
          : null; // Flex/Unassigned
      const targetJobId = jobId;

      if (!wcData.workersByPatternJob.has(targetSpId)) {
        wcData.workersByPatternJob.set(targetSpId, new Map());
      }
      const jobMap = wcData.workersByPatternJob.get(targetSpId)!;
      if (!jobMap.has(targetJobId)) {
        jobMap.set(targetJobId, new Map());
      }

      const workerKey = isClone
        ? `${worker.workerId}_moved_${cloneKeySuffix}`
        : worker.workerId;

      jobMap.get(targetJobId)!.set(workerKey, workerData);
    };

    // --- Process each filtered worker ---
    filteredWorkers.forEach((worker) => {
      const workerShifts = allShifts.filter(
        (shift) =>
          shift.workerId === worker.workerId &&
          workcenterDataMap.has(shift.uwCenterId)
      );


      // Get worker defaults
      const defaultWcId =
        worker.workCenterId && workcenterDataMap.has(worker.workCenterId)
          ? worker.workCenterId
          : null;
      const defaultPatternId = worker.patternId || null;
      const defaultJobId = worker.primaryJobId || null;

      // Find all "moved" shifts
      const movedShiftsMap = new Map<string, any[]>();
      let hasMovedShifts = false;

      workerShifts.forEach((shift) => {
        const assignedWcId = shift.uwCenterId!;
        const assignedPatternId = shift.uPatternId || null;
        const assignedJobId = shift.uPrimaryJobId || null;

        const isMoved =
          assignedWcId !== defaultWcId ||
          assignedPatternId !== defaultPatternId ||
          assignedJobId !== defaultJobId;

        if (isMoved) {
          hasMovedShifts = true;
          const assignmentKey = `${assignedWcId}-${assignedPatternId}-${assignedJobId}`;
          if (!movedShiftsMap.has(assignmentKey)) {
            movedShiftsMap.set(assignmentKey, []);
          }
          movedShiftsMap.get(assignmentKey)!.push(shift);
        }
      });

      // --- ** NEW LOGIC BRANCH ** ---
      if (isPrinting) {
        // --- PRINTING/EXPORT LOGIC ---
        // AC: Only show moved workers in new spot. Hide from old spot.
        if (hasMovedShifts) {
          // 1. Worker IS moved. Place *only* in new locations (as clones).
          movedShiftsMap.forEach((shifts, key) => {
            const [wcId, patternId, jobId] = key
              .split("-")
              .map((s) => (s === "null" ? null : Number(s)));
            placeWorker(
              worker,
              shifts,
              wcId!,
              patternId,
              jobId,
              true, // isClone = true
              key // cloneKeySuffix
            );
          });
        } else {
          // 2. Worker IS NOT moved. Place *only* in default location.
          if (defaultWcId) {
            placeWorker(
              worker,
              workerShifts, // Give it all its shifts
              defaultWcId,
              defaultPatternId,
              defaultJobId,
              false // isClone = false
            );
          }
        }
      } else {
        // --- NORMAL VIEW LOGIC ---
        // AC: Always show worker in default spot with ALL shifts. No duplicates.
        if (defaultWcId) {
          // Flag all shifts for the default view
          const flaggedShiftsForAll = workerShifts.map((shift) => {
            const assignedWcId = shift.uwCenterId!;
            const assignedPatternId = shift.uPatternId || null;
            const assignedJobId = shift.uPrimaryJobId || null;
            return {
              ...shift,
              isWorkcenterChanged: assignedWcId !== defaultWcId,
              isJobChanged: assignedJobId !== defaultJobId,
              isPatternChanged: assignedPatternId !== defaultPatternId,
            };
          });

          placeWorker(
            worker,
            flaggedShiftsForAll, // Normal view gets ALL shifts
            defaultWcId,
            defaultPatternId,
            defaultJobId,
            false // isClone = false
          );
        }
        // NO "Moved Placement" block here. This prevents duplicates.
      }
    }); // End filteredWorkers loop

    // --- 3. Final Conversion --- (Remains the Same)
    // This logic correctly processes the map generated by either branch
    const finalMapResult = Array.from(workcenterDataMap.values())
      .map((wcData) => {
        const finalShiftPatterns: any[] = [];
        const finalUnassignedWorkers: any[] = [];
        const finalFlexWorkers: any[] = [];

        wcData.workersByPatternJob.forEach((jobMap: any[], patternId: null) => {
          if (patternId !== null) {
            let spObj = finalShiftPatterns.find((sp) => sp.id === patternId);
            if (!spObj) {
              spObj = { id: patternId, workers: [] };
              finalShiftPatterns.push(spObj);
            }
            jobMap.forEach((workerMap) => {
              workerMap.forEach(
                (workerData: { workerId: any; shifts: any }) => {
                  if (
                    !spObj.workers.some(
                      (w: any) =>
                        w.workerId === workerData.workerId &&
                        JSON.stringify(w.shifts) ===
                        JSON.stringify(workerData.shifts)
                    )
                  ) {
                    spObj.workers.push(workerData);
                  }
                }
              );
            });
          } else {
            jobMap.forEach((workerMap) => {
              workerMap.forEach(
                (workerData: {
                  workerTypeId: any;
                  workerId: any;
                  shifts: any;
                }) => {
                  if (workerData.workerTypeId === flexTypeId) {
                    if (
                      !finalFlexWorkers.some(
                        (w: any) =>
                          w.workerId === workerData.workerId &&
                          JSON.stringify(w.shifts) ===
                          JSON.stringify(workerData.shifts)
                      )
                    ) {
                      finalFlexWorkers.push(workerData);
                    }
                  } else {
                    if (
                      !finalUnassignedWorkers.some(
                        (w: any) =>
                          w.workerId === workerData.workerId &&
                          JSON.stringify(w.shifts) ===
                          JSON.stringify(workerData.shifts)
                      )
                    ) {
                      finalUnassignedWorkers.push(workerData);
                    }
                  }
                }
              );
            });
          }
        });

        finalShiftPatterns.forEach((sp) => {
          const uniqueWorkers = Array.from(
            new Map(sp.workers.map((w: any) => [w.workerId, w])).values()
          );
          sp.totalWorkers = uniqueWorkers.length;
        });

        const nonEmptyShiftPatterns = finalShiftPatterns.filter(
          (sp) => sp.workers.length > 0
        );

        nonEmptyShiftPatterns.sort((a, b) =>
          (shiftPatternsObj[a.id]?.name ?? "").localeCompare(
            shiftPatternsObj[b.id]?.name ?? ""
          )
        );

        const finalUniqueWorkerIds = new Set([
          ...nonEmptyShiftPatterns.flatMap((sp) =>
            sp.workers.map((w: any) => w.workerId)
          ),
          ...finalUnassignedWorkers.map((w: any) => w.workerId),
          ...finalFlexWorkers.map((w: any) => w.workerId),
        ]);

        return {
          ...wcData,
          shiftPatterns: nonEmptyShiftPatterns,
          unassignedWorkers: finalUnassignedWorkers,
          flexWorkers: finalFlexWorkers,
          totalWorkers: finalUniqueWorkerIds.size, // ** This count is now correct
          shiftPatternIds: undefined,
          workersByPatternJob: undefined,
        };
      })
      .filter((wc) => wc.totalWorkers > 0);

    // Sort workcenters by name
    finalMapResult.sort((a, b) => {
      if (a.name && b.name) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

    return finalMapResult;
  }, [filteredWorkers, allShifts, allWorkcenters, selectedLocation, workerTypes, shiftPatternsObj, isPrinting]);

  // --- Rest of the controller (Callbacks, useEffects, return) remains the same ---
  const openWorkcenter = useCallback((workcenterId: number) => {
    localDispatch({ type: OPEN_WORKCENTER, payload: workcenterId });
  }, []);

  const closeWorkcenter = useCallback((workcenterId: number) => {
    localDispatch({ type: CLOSE_WORKCENTER, payload: workcenterId });
  }, []);

  const isWorkcenterOpen = useCallback(
    (workcenterId: number) => {
      return state.openWorkcenters.includes(workcenterId);
    },
    [state.openWorkcenters]
  );

  const isJobGroupOpen = useCallback(
    (jobGroupId: string) => {
      return state.openPrimaryJobs.includes(jobGroupId);
    },
    [state.openPrimaryJobs]
  );

  const openShiftPattern = useCallback((shiftPatternId: string) => {
    localDispatch({ type: OPEN_SHIFT_PATTERN, payload: shiftPatternId });
  }, []);

  const closeShiftPattern = useCallback((shiftPatternId: string) => {
    localDispatch({ type: CLOSE_SHIFT_PATTERN, payload: shiftPatternId });
  }, []);

  const openJobGroup = useCallback((jobGroupId: string) => {
    localDispatch({ type: OPEN_PRIMARY_JOB, payload: jobGroupId });
  }, []);

  const closeJobGroup = useCallback((jobGroupId: string) => {
    localDispatch({ type: CLOSE_PRIMARY_JOB, payload: jobGroupId });
  }, []);

  const isShiftPatternOpen = useCallback(
    (shiftPatternId: string) => {
      return state.openShiftPatterns.includes(shiftPatternId);
    },
    [state.openShiftPatterns]
  );
  // --- End Callbacks ---

  // --- useEffect Hooks --- Remain the Same ---
  useEffect(() => {
    if (workerWorkcenterShiftPatternsMap.length > 0) {
      const firstNonEmptyWC = workerWorkcenterShiftPatternsMap.find(
        (wc) => wc.totalWorkers > 0
      );
      const wcToOpen = firstNonEmptyWC || workerWorkcenterShiftPatternsMap[0];

      if (wcToOpen && !state.openWorkcenters.includes(wcToOpen.id)) {
        localDispatch({ type: OPEN_WORKCENTER, payload: wcToOpen.id });
        const unassignedId = `shiftpattern-${wcToOpen.id}-unassigned`;
        const flexId = `shiftpattern-${wcToOpen.id}-flex`;
        if (
          wcToOpen.unassignedWorkers?.length > 0 &&
          !state.openShiftPatterns.includes(unassignedId)
        ) {
          localDispatch({ type: OPEN_SHIFT_PATTERN, payload: unassignedId });
        }
        if (
          wcToOpen.flexWorkers?.length > 0 &&
          !state.openShiftPatterns.includes(flexId)
        ) {
          localDispatch({ type: OPEN_SHIFT_PATTERN, payload: flexId });
        }
        if (wcToOpen.shiftPatterns?.length > 0) {
          wcToOpen.shiftPatterns.forEach(
            ({ id, totalWorkers }: { id: string; totalWorkers: number }) => {
              const patternStringId = `shiftpattern-${wcToOpen.id}-${id}`;
              if (
                totalWorkers > 0 &&
                !state.openShiftPatterns.includes(patternStringId)
              ) {
                localDispatch({
                  type: OPEN_SHIFT_PATTERN,
                  payload: patternStringId,
                });
              }
            }
          );
        }
      }
    }
  }, [
    workerWorkcenterShiftPatternsMap,
    selectedLocation,
    state.openWorkcenters,
    state.openShiftPatterns,
  ]);

  useEffect(() => {
    if (fetchFTWorkerFiltersStatus === "fulfilled") {
      const {
        workerTypes,
        workerSubTypes,
        allWorkcenters,
        allManagers,
        workerStatuses,
        filterRelationships,
      } = filterData;

      const newFilters: Filter[] = [];

      if (workerTypes?.length > 0) {
        const workerTypeData = [
          ...workerTypes.map((type) => ({
            id: Number(type.id),
            name: type.name,
            originalType: type.type,
          })),
          ...(workerSubTypes?.map((subType) => ({
            id: Number(subType.id) + 1000,
            name: subType.name,
            parentId: Number(subType.workerTypeId),
          })) || []),
        ];

        newFilters.push({
          id: 0,
          name: WORKER_TYPES,
          key: FILTER_WORKER_TYPE_KEY,
          data: workerTypeData,
          reducedData: workerTypeData,
          searchedData: workerTypeData,
        });
      }

      const selectedLocationWorkcenters = allWorkcenters?.filter(
        (workcenter) => workcenter.locationId === selectedLocation
      );

      if (selectedLocationWorkcenters?.length > 0) {
        newFilters.push({
          id: 4,
          name: "Work Center",
          key: FILTER_WORK_CENTERS_KEY,
          data: selectedLocationWorkcenters,
          reducedData: selectedLocationWorkcenters,
          searchedData: selectedLocationWorkcenters,
        });
      }

      if (allManagers?.length > 0) {
        newFilters.push({
          id: 6,
          name: "Reporting Manager",
          key: FILTER_MANAGER_KEY,
          data: allManagers,
          reducedData: allManagers,
          searchedData: allManagers,
        });
      }
      setFilters(newFilters);
      setDataRelations(filterRelationships);
    }
  }, [
    filterData,
    fetchFTWorkerFiltersStatus,
    selectedLocation,
    setFilters,
    setDataRelations,
  ]);

  const defaultAppliedFilters = useMemo(() => {
    if (filterData.allManagers?.length > 0 && currentUserId) {
      const currentUserManager = filterData.allManagers.find(
        (manager) => manager.id.toString() === currentUserId.toString()
      );

      if (currentUserManager) {
        const hasData = filteredWorkers?.some(
          (worker) =>
            worker.managerUserId?.toString() === currentUserId.toString()
        );

        if (hasData) {
          return [
            {
              type: "Reporting Manager",
              key: FILTER_MANAGER_KEY,
              timeAdded: Date.now(),
              data: [
                {
                  id: currentUserManager.id,
                  timeAdded: Date.now(),
                  name: currentUserManager.name,
                },
              ],
            },
          ];
        }
      }
    }
    return [];
  }, [currentUserId, filterData.allManagers, filteredWorkers]);

  useEffect(() => {
    if (fetchFTWorkerFiltersStatus === "idle") {
      dispatch(fetchFTWorkerFilters());
    }
  }, [dispatch, fetchFTWorkerFiltersStatus]);

  useEffect(() => {
    if (selectedLocation && fetchFTWorkerFiltersStatus === "fulfilled") {
      setAppliedFilters([]);
      dispatch(fetchFTWorkerShifts("INITIAL"));
    }
  }, [
    dispatch,
    selectedLocation,
    fetchFTWorkerFiltersStatus,
    setAppliedFilters,
  ]);

  useEffect(() => {
    dispatch(setSearchTerm(""));
  }, [dispatch]);

  return [
    {
      appliedFilters,
      fetchFTWorkerFiltersStatus,
      startingDay,
      isShiftPatternOpen,
      isWorkcenterOpen,
      isJobGroupOpen,
      shiftPatternsObj,
      viewMode,
      fetchFTWorkerShiftsStatus,
      filters,
      isFilterOpen: open,
      defaultAppliedFilters,
      workerWorkcenterShiftPatternsMap,
      selectedLocation,
    },
    {
      openShiftPattern,
      openWorkcenter,
      closeShiftPattern,
      closeWorkcenter,
      openJobGroup,
      closeJobGroup,
      setFilters,
      setIsFilterOpen: setFilterOpen,
      setAppliedFilters,
    },
  ] as const;
};

export default useWorkerSchedulerController;